/**
 * GeneratorsPage — Top-level Generators group (case-picker, no open case required)
 *
 * Features:
 * - Case picker at top: dropdown/search of all cases
 * - Renders the same 5 generator components once a case is selected
 * - Without a case selected, shows EmptyState prompting case selection
 * - Preserves the same #demand-letter etc. anchor IDs for deep-links
 *
 * Design spec compliance:
 *   - CSS variable tokens ONLY (no literal hex values)
 *   - Near-black surfaces, orange brand accent
 *   - DM Serif Display / Outfit / DM Mono type stack
 */

import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ScrollText,
  ShieldAlert,
  Gavel,
  Scale,
  Loader2,
  ChevronDown,
  Briefcase,
  AlertTriangle,
} from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { ToolTile } from '@/components/ui/ToolTile';
import { getAuthHeaders } from '@/lib/authHeaders';

// Lazy imports for heavy generator components
const DemandLetterGenerator = React.lazy(() => import('@/pages/DemandLetterGenerator'));
const ProofOfLossGenerator = React.lazy(() => import('@/pages/ProofOfLossGenerator'));
const EUOPrepMemoGenerator = React.lazy(() => import('@/pages/EUOPrepMemoGenerator'));
const RoRResponseGenerator = React.lazy(() => import('@/pages/RoRResponseGenerator'));
const RoRAnalysisMemoGenerator = React.lazy(() => import('@/pages/RoRAnalysisMemoGenerator'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('GeneratorsPage component error:', error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function ComponentError({ name }: { name: string }): React.JSX.Element {
  return (
    <div className="p-8 text-center">
      <div className="mb-2 flex justify-center" style={{ color: 'var(--color-text-muted)' }}><AlertTriangle className="w-6 h-6" aria-hidden="true" /></div>
      <h3 className="cs-h3 mb-1">
        Failed to load {name}
      </h3>
      <p className="cs-meta">
        The component could not be loaded. It may not exist yet or there was an error.
      </p>
    </div>
  );
}

interface Case {
  id: string;
  client_name: string;
  claim_number?: string;
  carrier?: string;
  status?: string;
}

export default function GeneratorsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);

  // Load cases on mount
  useEffect(() => {
    const fetchCases = async () => {
      const authHeaders = await getAuthHeaders();
      if (!authHeaders.Authorization) {
        setError('Please log in to access the generators.');
        setLoading(false);
        return;
      }

      try {
        // Try to fetch attorney portfolio
        const response = await fetch(`${BACKEND_URL}/api/v1/attorney/portfolio`, {
          headers: authHeaders,
        });

        if (response.status === 401 || response.status === 403) {
          setError('Session expired. Please log in again.');
          setLoading(false);
          return;
        }

        if (response.ok) {
          const data = await response.json();
          // Handle different response shapes
          const caseList = [data?.cases, data?.reports, data].find(Array.isArray) ?? [];
          // Normalize the attorney/portfolio payload onto the fields this page
          // reads. The backend (_build_case_payload) emits claim_id/claimant_name
          // and a {key,label} status object — NOT id/client_name — so without
          // this map the picker labels are blank and selectedCase.id (passed to
          // the generators) is undefined. Mirrors AttorneyWorkDesk's normalization.
          const normalizedCases: Case[] = (caseList as any[]).map((c) => ({
            id: c.id ?? c.claim_id ?? c.case_id ?? c._id ?? '',
            client_name: c.client_name ?? c.claimant_name ?? 'Unknown',
            claim_number: c.claim_number ?? c.policy_number ?? undefined,
            carrier: c.carrier ?? undefined,
            status: typeof c.status === 'string' ? c.status : (c.status?.label ?? c.status?.key ?? undefined),
          }));
          setCases(normalizedCases);
        } else {
          setError('Failed to load cases');
        }
      } catch (err: any) {
        console.error('Failed to fetch cases:', err);
        setError(err.message || 'Failed to load cases');
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [navigate]);

  // Handle case selection
  const handleSelectCase = (caseItem: Case) => {
    setSelectedCase(caseItem);
    setShowCaseDropdown(false);
  };

  // Clear case selection
  const handleClearCase = () => {
    setSelectedCase(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-brand)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center p-6">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
          <h2 className="cs-h3 mb-2">Error</h2>
          <p className="cs-body mb-4">{error}</p>
          <button
            onClick={() => navigate('/attorney/login')}
            className="px-4 py-2 bg-[var(--color-brand)] rounded-lg hover:opacity-90"
            style={{ color: 'var(--color-on-brand)' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]" data-testid="generators-page">
      {/* Header with Case Picker */}
      <header className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="cs-display">
                Document Generators
              </h1>
              <p className="cs-body mt-1">
                Generate demand letters, proofs of loss, and other legal documents
              </p>
            </div>

            {/* Case Picker */}
            <div className="relative">
              {selectedCase ? (
                <div className="flex items-center gap-2">
                  <div className="px-4 py-2 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-lg flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[var(--color-brand)]" />
                    <span className="cs-body" style={{ color: 'var(--color-text-primary)' }}>
                      {selectedCase.client_name}
                    </span>
                    {selectedCase.claim_number && (
                      <span className="cs-meta">
                        ({selectedCase.claim_number})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleClearCase}
                    className="px-3 py-2 cs-body hover:text-[var(--color-text-primary)]"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setShowCaseDropdown(!showCaseDropdown)}
                    className="px-4 py-2 bg-[var(--color-brand)] rounded-lg flex items-center gap-2 hover:opacity-90"
                    style={{ color: 'var(--color-on-brand)' }}
                    data-testid="case-picker-btn"
                  >
                    <Briefcase className="w-4 h-4" />
                    Select a Case
                    <ChevronDown className={`w-4 h-4 transition-transform ${showCaseDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown */}
                  {showCaseDropdown && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowCaseDropdown(false)}
                      />
                      {/* Dropdown Menu */}
                      <div className="absolute right-0 top-full mt-2 w-80 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg shadow-xl z-50 max-h-96 overflow-auto"
                        data-testid="case-dropdown"
                      >
                        <div className="p-2 border-b border-[var(--color-border)]">
                          <p className="cs-label px-2 py-1">
                            Select a case to work with
                          </p>
                        </div>
                        {cases.length === 0 ? (
                          <div className="p-4 text-center cs-meta">
                            No cases found
                          </div>
                        ) : (
                          <div className="py-1">
                            {cases.map((caseItem) => (
                              <button
                                key={caseItem.id}
                                onClick={() => handleSelectCase(caseItem)}
                                className="w-full px-4 py-3 text-left hover:bg-[var(--color-bg-hover)] transition-colors"
                              >
                                <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                                  {caseItem.client_name}
                                </div>
                                <div className="cs-meta mt-0.5">
                                  {caseItem.claim_number && `Claim: ${caseItem.claim_number}`}
                                  {caseItem.carrier && ` • ${caseItem.carrier}`}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {!selectedCase ? (
          <EmptyState
            icon={<Briefcase className="w-12 h-12 text-[var(--color-text-muted)]" />}
            title="Select a Case"
            description="Choose a case from the dropdown above to start generating documents."
            action={
              <button
                onClick={() => setShowCaseDropdown(true)}
                className="px-4 py-2 bg-[var(--color-brand)] rounded-lg hover:opacity-90"
                style={{ color: 'var(--color-on-brand)' }}
              >
                Select Case
              </button>
            }
          />
        ) : (
          <div className="space-y-8">
            {/* Generator Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <ToolTile
                title="Demand Letter"
                description="Generate professional demand letters with state-specific statutory references"
                icon={<FileText className="w-6 h-6" />}
                onClick={() => document.getElementById('demand-letter')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[var(--color-bg-surface)] border-[var(--color-border)]"
              />
              <ToolTile
                title="Proof of Loss"
                description="Create sworn statements of proof of loss with jurat and signature blocks"
                icon={<ScrollText className="w-6 h-6" />}
                onClick={() => document.getElementById('proof-of-loss')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[var(--color-bg-surface)] border-[var(--color-border)]"
              />
              <ToolTile
                title="EUO Prep Memo"
                description="Prepare examination under oath prep memos for client preparation"
                icon={<ShieldAlert className="w-6 h-6" />}
                onClick={() => document.getElementById('euo-prep-memo')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[var(--color-bg-surface)] border-[var(--color-border)]"
              />
              <ToolTile
                title="RoR Response"
                description="Generate reservation of rights response letters"
                icon={<Gavel className="w-6 h-6" />}
                onClick={() => document.getElementById('ror-response')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[var(--color-bg-surface)] border-[var(--color-border)]"
              />
              <ToolTile
                title="RoR Analysis Memo"
                description="Create coverage analysis memos for reservation of rights issues"
                icon={<Scale className="w-6 h-6" />}
                onClick={() => document.getElementById('ror-analysis')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[var(--color-bg-surface)] border-[var(--color-border)]"
              />
            </div>

            {/* Generator Sections */}
            <div className="space-y-8">
              {/* Demand Letter */}
              <section id="demand-letter" className="scroll-mt-24">
                <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                    <h2 className="cs-h3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[var(--color-brand)]" />
                      Demand Letter Generator
                    </h2>
                  </div>
                  <div className="p-4">
                    <Suspense fallback={<div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
                      <ErrorBoundary fallback={<ComponentError name="Demand Letter Generator" />}>
                        <DemandLetterGenerator caseId={selectedCase.id} />
                      </ErrorBoundary>
                    </Suspense>
                  </div>
                </div>
              </section>

              {/* Proof of Loss */}
              <section id="proof-of-loss" className="scroll-mt-24">
                <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                    <h2 className="cs-h3 flex items-center gap-2">
                      <ScrollText className="w-5 h-5 text-[var(--color-brand)]" />
                      Proof of Loss Generator
                    </h2>
                  </div>
                  <div className="p-4">
                    <Suspense fallback={<div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
                      <ErrorBoundary fallback={<ComponentError name="Proof of Loss Generator" />}>
                        <ProofOfLossGenerator caseId={selectedCase.id} />
                      </ErrorBoundary>
                    </Suspense>
                  </div>
                </div>
              </section>

              {/* EUO Prep Memo */}
              <section id="euo-prep-memo" className="scroll-mt-24">
                <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                    <h2 className="cs-h3 flex items-center gap-2">
                      <ShieldAlert className="w-5 h-5 text-[var(--color-brand)]" />
                      EUO Prep Memo Generator
                    </h2>
                  </div>
                  <div className="p-4">
                    <Suspense fallback={<div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
                      <ErrorBoundary fallback={<ComponentError name="EUO Prep Memo Generator" />}>
                        <EUOPrepMemoGenerator caseId={selectedCase.id} />
                      </ErrorBoundary>
                    </Suspense>
                  </div>
                </div>
              </section>

              {/* RoR Response */}
              <section id="ror-response" className="scroll-mt-24">
                <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                    <h2 className="cs-h3 flex items-center gap-2">
                      <Gavel className="w-5 h-5 text-[var(--color-brand)]" />
                      RoR Response Generator
                    </h2>
                  </div>
                  <div className="p-4">
                    <Suspense fallback={<div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
                      <ErrorBoundary fallback={<ComponentError name="RoR Response Generator" />}>
                        <RoRResponseGenerator caseId={selectedCase.id} />
                      </ErrorBoundary>
                    </Suspense>
                  </div>
                </div>
              </section>

              {/* RoR Analysis */}
              <section id="ror-analysis" className="scroll-mt-24">
                <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                    <h2 className="cs-h3 flex items-center gap-2">
                      <Scale className="w-5 h-5 text-[var(--color-brand)]" />
                      RoR Analysis Memo Generator
                    </h2>
                  </div>
                  <div className="p-4">
                    <Suspense fallback={<div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
                      <ErrorBoundary fallback={<ComponentError name="RoR Analysis Memo Generator" />}>
                        <RoRAnalysisMemoGenerator caseId={selectedCase.id} />
                      </ErrorBoundary>
                    </Suspense>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
